import { useState, useRef, useEffect, useCallback } from 'react';
import {
  FaSatelliteDish,
  FaShieldHalved,
  FaArrowLeft,
  FaCircleCheck,
  FaCircleXmark,
  FaRotateRight,
} from 'react-icons/fa6';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

/* ============================================
   HOOK: Network Canvas
   ============================================ */
function useNetworkCanvas(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let mouse = { x: -9999, y: -9999 };

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const count = Math.min(55, Math.floor((canvas.width * canvas.height) / 22000));
    const nodes = Array.from({ length: count }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.6 + 0.5,
      o: Math.random() * 0.3 + 0.08,
    }));

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);

    const maxD = 120;
    const mouseD = 170;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        const dm = Math.hypot(mouse.x - n.x, mouse.y - n.y);
        let op = n.o;
        let rad = n.r;
        if (dm < mouseD) {
          op = Math.min(1, op + (1 - dm / mouseD) * 0.5);
          rad += (1 - dm / mouseD) * 2;
        }
        ctx.beginPath();
        ctx.arc(n.x, n.y, rad, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,180,216,${op})`;
        ctx.fill();
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
          if (d < maxD) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(0,180,216,${(1 - d / maxD) * 0.08})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
        const dm = Math.hypot(mouse.x - nodes[i].x, mouse.y - nodes[i].y);
        if (dm < mouseD) {
          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.strokeStyle = `rgba(72,202,228,${(1 - dm / mouseD) * 0.18})`;
          ctx.lineWidth = 0.6;
          ctx.stroke();
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  }, [canvasRef]);
}

/* ============================================
   HOOK: Countdown Timer
   ============================================ */
function useCountdown(initialSeconds, onExpire) {
  const [seconds, setSeconds] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(true);

  useEffect(() => {
    if (!isRunning || seconds <= 0) return;
    const interval = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          setIsRunning(false);
          onExpire?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isRunning, seconds, onExpire]);

  const reset = useCallback((newSeconds) => {
    setSeconds(newSeconds ?? initialSeconds);
    setIsRunning(true);
  }, [initialSeconds]);

  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return {
    formatted: `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`,
    seconds,
    isRunning,
    isExpired: !isRunning && seconds === 0,
    reset,
  };
}

/* ============================================
   KOMPONEN: OtpInput (6 digit blocks)
   ============================================ */
function OtpInput({ value, onChange, error, disabled }) {
  const inputRefs = useRef([])
  const CODE_LENGTH = 6

  // Focus input pertama saat mount
  useEffect(() => {
    if (!disabled && inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [disabled])

  const handleChange = (index, e) => {
    const raw = e.target.value.replace(/\D/g, '')
    const digit = raw.slice(-1) // Ambil hanya 1 digit terakhir

    if (!digit) return

    const newCode = [...value]
    newCode[index] = digit
    onChange(newCode)

    // Auto-focus ke input berikutnya
    if (index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const newCode = [...value]

      if (value[index]) {
        // Jika ada digit, hapus dulu
        newCode[index] = ''
        onChange(newCode)
      } else if (index > 0) {
        // Jika kosong, pindah ke sebelumnya dan hapus
        newCode[index - 1] = ''
        onChange(newCode)
        inputRefs.current[index - 1]?.focus()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      e.preventDefault()
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH)
    if (!pasted) return

    const newCode = [...value]
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i]
    }
    onChange(newCode)

    // Focus ke input setelah digit terakhir yang diisi, atau input terakhir
    const nextIndex = Math.min(pasted.length, CODE_LENGTH - 1)
    inputRefs.current[nextIndex]?.focus()
  }

  const handleFocus = (index) => {
    // Select semua text saat focus untuk memudahkan replace
    inputRefs.current[index]?.select()
  }

  const isComplete = value.every((d) => d !== '')
  const hasError = error && !isComplete

  return (
    <div className="flex justify-center gap-2.5 sm:gap-3.5" role="group" aria-label="Kode verifikasi 6 digit">
      {value.map((digit, i) => {
        const isFilled = digit !== ''
        const isError = error && !isComplete
        const isSuccess = isComplete && !error

        return (
          <div key={i} className="relative">
            <input
              ref={(el) => { inputRefs.current[i] = el }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              onFocus={() => handleFocus(i)}
              disabled={disabled}
              autoComplete="one-time-code"
              aria-label={`Digit ${i + 1}`}
              className={`
                w-12 h-14 sm:w-14 sm:h-16
                text-center text-xl sm:text-2xl font-display font-bold
                rounded-xl transition-all duration-300 outline-none
                select-none caret-ocean-500
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'}
                ${isError
                  ? 'bg-red-500/8 border-2 border-red-500/40 text-white focus:border-red-400 focus:ring-2 focus:ring-red-500/20'
                  : isSuccess
                    ? 'bg-ocean-800/15 border-2 border-ocean-600/50 text-ocean-400'
                    : isFilled
                      ? 'bg-navy-700/60 border-2 border-ocean-600/40 text-white focus:border-ocean-500 focus:ring-2 focus:ring-ocean-600/20'
                      : 'bg-navy-700/50 border-2 border-navy-500/40 text-white placeholder-transparent focus:border-ocean-600/60 focus:ring-2 focus:ring-ocean-600/20'
                }
              `}
            />
            {/* Filled dot indicator */}
            {isFilled && !error && (
              <span
                className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-ocean-500 transition-all duration-300"
                aria-hidden="true"
              />
            )}
            {isFilled && error && (
              <span
                className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-red-400 transition-all duration-300"
                aria-hidden="true"
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ============================================
   KOMPONEN: VerifyEmail
   ============================================ */
export const EmailVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  useNetworkCanvas(canvasRef);

  // Ambil email dari location state atau fallback ke localStorage
  const email = location.state?.email || localStorage.getItem('verify_email') || '';

  const [code, setCode] = useState(Array(6).fill(''))
  const [error, setError] = useState('')
  const [status, setStatus] = useState('idle') // idle | verifying | success | error
  const [mounted, setMounted] = useState(false);

  const CODE_EXPIRY_SECONDS = 5 * 60 // 5 menit

  const timer = useCountdown(CODE_EXPIRY_SECONDS, () => {
    // Timer habis
  })

  useEffect(() => {
    // Simpan email ke localStorage jika ada
    if (email && location.state?.email) {
      localStorage.setItem('verify_email', email)
    }
    const t = setTimeout(() => setMounted(true), 100)
    return () => clearTimeout(t)
  }, [email, location.state?.email])

  const codeString = code.join('')
  const isComplete = code.every((d) => d !== '')

  const handleVerify = async () => {
    if (!isComplete) {
      setError('Masukkan 6 digit kode verifikasi')
      return
    }

    setError('')
    setStatus('verifying')

    try {
      navigate(`/create-new-password?email=${email}&code=${codeString}`)
      // await AuthService.verifyEmail({
      //   data: { email, code: codeString },
      // })
      // setStatus('success')
      // localStorage.removeItem('verify_email')

      // // Redirect ke reset password setelah 1.5 detik
      // setTimeout(() => {
      //   navigate('/reset-password', { state: { email, code: codeString } })
      // }, 1500)
    } catch (err) {
      // setStatus('error')
      // const msg =
      //   err?.response?.data?.message ||
      //   err?.response?.data?.error ||
      //   'Kode verifikasi tidak valid atau sudah kadaluarsa.'
      // setError(msg)
    }
  }

  const handleResend = async () => {
    setError('')
    setCode(Array(6).fill(''))
    setStatus('idle')

    try {
      // await AuthService.forgotPassword({ data: { email } })
      // timer.reset(CODE_EXPIRY_SECONDS)
    } catch (err) {
      // const msg =
      //   err?.response?.data?.message ||
      //   'Gagal mengirim ulang kode. Silakan coba lagi.'
      // setError(msg)
    }
  }

  // Auto-submit ketika 6 digit sudah terisi
  useEffect(() => {
    if (isComplete && status === 'idle') {
      const timeout = setTimeout(() => {
        handleVerify()
      }, 300)
      return () => clearTimeout(timeout)
    }
  }, [isComplete]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Helmet>
        <title>Verifikasi Email | Radius Trend - Billing Management System</title>
        <meta
          name="description"
          content="Masukkan kode verifikasi 6 digit yang dikirim ke email Anda untuk melanjutkan proses reset password."
        />
      </Helmet>

      <main className="relative min-h-screen flex items-center justify-center bg-navy-900 p-4 sm:p-6 overflow-hidden">
        {/* ===== Network Canvas ===== */}
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full z-0"
          aria-hidden="true"
        />

        {/* ===== Gradient Orbs ===== */}
        <div
          className="absolute w-[450px] h-[450px] bg-ocean-900/25 -top-[15%] -left-[8%] rounded-full"
          style={{ filter: 'blur(100px)', animation: 'float 9s ease-in-out infinite' }}
          aria-hidden="true"
        />
        <div
          className="absolute w-[350px] h-[350px] bg-ocean-800/15 -bottom-[10%] -right-[5%] rounded-full"
          style={{ filter: 'blur(90px)', animation: 'floatSlow 11s ease-in-out infinite' }}
          aria-hidden="true"
        />
        <div
          className="absolute w-[220px] h-[220px] bg-navy-500/15 top-[45%] right-[18%] rounded-full"
          style={{ filter: 'blur(70px)', animation: 'float 13s ease-in-out 2s infinite' }}
          aria-hidden="true"
        />

        {/* ===== Card ===== */}
        <article
          className={`
            relative z-10 w-full max-w-md
            rounded-3xl overflow-hidden
            transition-all duration-1000 cubic-bezier(0.16, 1, 0.3, 1)
            ${mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-[0.97]'}
          `}
          style={{
            background: 'rgba(11, 20, 38, 0.65)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(0, 180, 216, 0.1)',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 80px rgba(0, 180, 216, 0.06)',
          }}
        >
          {/* Top line */}
          <div
            className="h-px w-full"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(0, 180, 216, 0.3), transparent)',
            }}
            aria-hidden="true"
          />

          <div className="p-8 sm:p-10">
            {/* ===== Header ===== */}
            <header className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.2), rgba(0, 119, 182, 0.1))',
                    border: '1px solid rgba(0, 180, 216, 0.2)',
                    boxShadow: '0 8px 24px rgba(0, 180, 216, 0.15)',
                  }}
                >
                  <FaShieldHalved
                    className="text-2xl text-ocean-500"
                    aria-hidden="true"
                  />
                </div>
              </div>

              <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2 tracking-tight">
                {status === 'success' ? 'Verifikasi Berhasil' : 'Verifikasi Email'}
              </h1>
              <p className="text-sm text-slate-custom leading-relaxed">
                {status === 'success'
                  ? 'Mengalihkan ke halaman reset password...'
                  : `Masukkan kode 6 digit yang dikirim ke:`}
              </p>

              {/* Email display */}
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg" style={{
                background: 'rgba(0, 180, 216, 0.06)',
                border: '1px solid rgba(0, 180, 216, 0.12)',
              }}>
                <span className="text-ocean-400 text-sm font-semibold break-all">{email}</span>
              </div>
            </header>

            {/* ===== Success State ===== */}
            {status === 'success' && (
              <div
                className="mb-6 flex flex-col items-center gap-4"
                style={{ animation: 'fadeIn 0.5s ease-out' }}
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '2px solid rgba(16, 185, 129, 0.3)',
                    animation: 'scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                >
                  <FaCircleCheck className="text-3xl text-emerald-400" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-emerald-400 text-sm font-medium">
                    Mengalihkan...
                  </span>
                </div>
              </div>
            )}

            {/* ===== Error Banner ===== */}
            {status === 'error' && error && (
              <div
                className="mb-6 p-4 rounded-xl flex items-start gap-3"
                style={{
                  background: 'rgba(239, 68, 68, 0.06)',
                  border: '1px solid rgba(239, 68, 68, 0.15)',
                  animation: 'fadeIn 0.4s ease-out',
                }}
                role="alert"
              >
                <FaCircleXmark className="text-red-400 text-lg flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-400 text-sm leading-relaxed">{error}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setCode(Array(6).fill(''))
                      setError('')
                      setStatus('idle')
                    }}
                    className="text-xs text-ocean-500 font-semibold mt-2 hover:text-ocean-400 transition-colors inline-flex items-center gap-1"
                  >
                    <FaRotateRight className="text-[10px]" />
                    Coba lagi
                  </button>
                </div>
              </div>
            )}

            {/* ===== OTP Input ===== */}
            {status !== 'success' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleVerify()
                }}
                className="space-y-6"
                noValidate
              >
                {/* 6-digit input */}
                <div>
                  <OtpInput
                    value={code}
                    onChange={setCode}
                    error={status === 'error' ? error : ''}
                    disabled={status === 'verifying'}
                  />
                  {/* Inline error untuk kode belum lengkap */}
                  {error && status !== 'error' && (
                    <p className="text-xs text-red-400 font-medium mt-3 text-center flex items-center justify-center gap-1.5">
                      <span className="inline-block w-1 h-1 rounded-full bg-red-400" aria-hidden="true" />
                      {error}
                    </p>
                  )}
                </div>

                {/* Verify Button */}
                <button
                  type="submit"
                  disabled={!isComplete || status === 'verifying'}
                  className="btn-primary w-full justify-center text-base py-4"
                >
                  {status === 'verifying' ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Memverifikasi...</span>
                    </>
                  ) : (
                    <>
                      <span>Verifikasi Kode</span>
                      <FaShieldHalved className="text-base" />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* ===== Timer & Resend ===== */}
            <div className="mt-8 text-center">
              {timer.isRunning && status !== 'success' ? (
                <div className="space-y-1">
                  <p className="text-xs text-slate-custom/60">
                    Kode berlaku selama
                  </p>
                  <p className="font-display text-2xl font-bold text-ocean-500 tracking-widest">
                    {timer.formatted}
                  </p>
                </div>
              ) : status !== 'success' ? (
                <div
                  style={{ animation: 'fadeIn 0.5s ease-out' }}
                >
                  <p className="text-xs text-slate-custom/60 mb-3">
                    Kode sudah kadaluarsa
                  </p>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={false}
                    className="btn-secondary text-xs py-2.5 px-5"
                  >
                    <FaRotateRight className="text-xs" />
                    <span>Kirim Ulang Kode</span>
                  </button>
                </div>
              ) : null}
            </div>

            {/* ===== Back to Login ===== */}
            <footer className="mt-8 pt-6 border-t border-navy-500/20 text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-2 text-sm text-slate-custom hover:text-ocean-500 transition-colors font-medium"
              >
                <FaArrowLeft className="text-xs" />
                <span>Kembali ke Login</span>
              </button>
            </footer>
          </div>

          {/* Bottom line */}
          <div
            className="h-px w-full"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(0, 180, 216, 0.2), transparent)',
            }}
            aria-hidden="true"
          />
        </article>

        {/* ===== Copyright ===== */}
        <p
          className="absolute bottom-4 left-0 right-0 text-center text-xs text-slate-custom/30 z-10"
          style={{ animation: 'fadeIn 1s ease-out 1s both' }}
        >
          &copy; {new Date().getFullYear()} Radius Trend. Seluruh hak cipta dilindungi.
        </p>
      </main>

      {/* ===== Inline Styles ===== */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(3deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.5); }
          to { opacity: 1; transform: scale(1); }
        }
        .font-display { font-family: 'Space Grotesk', sans-serif; }
        .font-body { font-family: 'Plus Jakarta Sans', sans-serif; }
        body { font-family: 'Plus Jakarta Sans', sans-serif; }

        .bg-navy-900 { background-color: #060D1F; }
        .bg-navy-700\/50 { background-color: rgba(15, 26, 46, 0.5); }
        .bg-navy-700\/60 { background-color: rgba(15, 26, 46, 0.6); }
        .bg-ocean-800\/15 { background-color: rgba(0, 95, 140, 0.15); }
        .border-navy-500\/40 { border-color: rgba(26, 45, 74, 0.4); }
        .border-navy-500\/20 { border-color: rgba(26, 45, 74, 0.2); }
        .text-slate-custom { color: #8899AA; }
        .text-slate-custom\/60 { color: rgba(136, 153, 170, 0.6); }
        .text-slate-custom\/30 { color: rgba(136, 153, 170, 0.3); }
        .text-ocean-500 { color: #48CAE4; }
        .text-ocean-400 { color: #90E0EF; }
        .text-ocean-600 { color: #00B4D8; }
        .text-ocean-700 { color: #0096C7; }
        .text-red-400 { color: #f87171; }
        .text-emerald-400 { color: #34d399; }
        .border-ocean-600\/60 { border-color: rgba(0, 180, 216, 0.6); }
        .border-ocean-600\/50 { border-color: rgba(0, 180, 216, 0.5); }
        .border-ocean-600\/40 { border-color: rgba(0, 180, 216, 0.4); }
        .border-red-500\/40 { border-color: rgba(239, 68, 68, 0.4); }
        .border-red-400 { border-color: rgba(248, 113, 113, 1); }
        .ring-ocean-600\/20 { --tw-ring-color: rgba(0, 180, 216, 0.2); }
        .ring-red-500\/20 { --tw-ring-color: rgba(239, 68, 68, 0.2); }
        .bg-red-500\/8 { background-color: rgba(239, 68, 68, 0.08); }
        .focus\:border-ocean-600\/60:focus { border-color: rgba(0, 180, 216, 0.6); }
        .focus\:border-ocean-500:focus { border-color: #48CAE4; }
        .focus\:border-red-400:focus { border-color: rgba(248, 113, 113, 1); }
        .focus\:ring-ocean-600\/20:focus { --tw-ring-color: rgba(0, 180, 216, 0.2); }
        .focus\:ring-red-500\/20:focus { --tw-ring-color: rgba(239, 68, 68, 0.2); }
        .hover\:text-ocean-500:hover { color: #48CAE4; }
        .hover\:text-ocean-400:hover { color: #90E0EF; }
        .placeholder-transparent::placeholder { color: transparent; }
        .caret-ocean-500 { caret-color: #48CAE4; }
        .select-none { -webkit-user-select: none; user-select: none; }

        .btn-primary {
          background: linear-gradient(135deg, #0077B6, #00B4D8);
          color: #fff;
          padding: 14px 32px;
          border-radius: 12px;
          font-weight: 600;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          border: none;
          cursor: pointer;
          font-size: inherit;
          font-family: inherit;
        }
        .btn-primary::before {
          content: '';
          position: absolute;
          top: 0; left: -100%;
          width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          transition: left 0.6s ease;
        }
        .btn-primary:hover::before { left: 100%; }
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(0, 180, 216, 0.4);
        }
        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none !important;
          box-shadow: none !important;
        }
        .btn-primary:disabled::before { display: none; }

        .btn-secondary {
          background: transparent;
          color: #48CAE4;
          padding: 14px 32px;
          border-radius: 12px;
          font-weight: 600;
          border: 2px solid rgba(0, 180, 216, 0.35);
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          cursor: pointer;
          font-size: inherit;
          font-family: inherit;
        }
        .btn-secondary:hover {
          background: rgba(0, 180, 216, 0.08);
          border-color: #00B4D8;
          transform: translateY(-2px);
        }

        /* Hide number input spinners */
        input[type="text"][inputmode="numeric"]::-webkit-inner-spin-button,
        input[type="text"][inputmode="numeric"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="text"][inputmode="numeric"] {
          -moz-appearance: textfield;
        }
      `}</style>
    </>
  );
};